export default function ParklandDestinationsSection() {
  const stats = [
    {
      value: "48%",
      label: "of Parkland requests are family Caribbean cruise or resort departures with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for family spring break and summer vacation bookings from Parkland households",
    },
    {
      value: "Feb–May",
      label: "peak passport demand — spring break, bar/bat mitzvah season, and summer booking window",
    },
    {
      value: "36-hour",
      label: "shortest cruise departure window we've accommodated for a Parkland family",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Israel",
      context: "A significant destination for Parkland's Jewish community — family visits to Jerusalem, Tel Aviv, and Haifa, bar and bat mitzvah trips, religious pilgrimage, and cultural tourism. Many Parkland families travel to Israel annually or bi-annually for lifecycle events and holidays. Israel requires six months of passport validity beyond your planned departure date.",
      urgency: "Bar and bat mitzvah trips have fixed religious calendar dates — passport delays can affect months of planning and family coordination",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common destination for Parkland families — whether by cruise from Port Everglades or Fort Lauderdale, or by direct flight from FLL or PBI to Nassau, Grand Cayman, or Jamaica. Spring break and summer cruise itineraries are staples of the Parkland family vacation calendar. All cruise passengers and most air travelers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Los Cabos are popular for Parkland family beach vacations and all-inclusive resort trips. FLL offers direct and connecting service to Cancun, while PBI provides seasonal options. Mexico requires six months of passport validity for entry and is a staple spring break destination for Parkland families.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March before Cancun departures",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe",
      context: "Paris, London, Rome, Barcelona, and Amsterdam draw Parkland families for summer cultural tours, educational trips, and heritage travel. Students from Marjory Stoneman Douglas participate in exchange programs in Spain, France, and Italy. The Schengen Area requires three months of validity beyond planned departure.",
      urgency: "Summer Europe trips are often booked 2–3 months in advance — passport discoveries in April or May create compressed timelines",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Latin America",
      context: "Colombia, Brazil, Argentina, and Panama attract Parkland business travelers and families with heritage connections. Bogota and Medellin are popular for family visits among Parkland's Latin American community. Brazil requires visas for U.S. citizens, adding complexity to the passport timeline.",
      urgency: "Business trips to Latin America are often booked with 2–4 weeks of notice — too short for standard processing plus visa requirements",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Montreal, and Vancouver draw Parkland travelers for summer family trips, ski vacations in Banff and Whistler, and business conferences. Canada requires a valid passport for all air arrivals, and many Parkland families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Summer Canada trips and winter ski vacations are planned 1–2 months ahead — tight for standard passport processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Parkland Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Parkland
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Parkland travelers head to Israel, the Caribbean, Europe, Mexico, and Canada for family vacations, religious travel, educational trips, and cruises. These are the destinations that create the most passport urgency for our Parkland clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Coral Springs", href: "/cities/coral-springs/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "Weston", href: "/cities/weston/" },
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach/" },
              { name: "Aventura", href: "/cities/aventura/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}